// src/utils/bracketUtils.ts
import { BracketGame, Region, Team, WinnerWithMetadata, WinnerSource } from '../types/types';

// Export to indicate we're not using this file for round names anymore
export const ROUND_NAMES_NOW_IN_CONTEXT = true;

/**
 * Helper function to get seed for a team name.
 */
export const getTeamSeed = (teams: Team[], teamName: string): number => {
  const team = teams.find(t => t.name === teamName);
  return team ? team.seed : 0;
};

/**
 * Determines if a team in a game is from a known winner path.
 */
export const isTeamFromKnownWinnerPath = (
  teamName: string,
  gameId: number,
  knownWinners: WinnerWithMetadata[],
  games: BracketGame[]
): boolean => {
  // Find the source game for this team
  const potentialSourceGames = games.filter(g => g.nextGameId === gameId);

  // Check if this team is a winner from any of those games
  for (const sourceGame of potentialSourceGames) {
    if (sourceGame.winner === teamName) {
      // Check if this source game has a known OFFICIAL winner only
      const winnerData = sourceGame.id < knownWinners.length ? knownWinners[sourceGame.id] : null;
      return !!winnerData && winnerData.team === teamName && winnerData.source === WinnerSource.OFFICIAL;
    }
  }

  return false;
};

/**
 * Helper function to find source game for a team, improved to take games array as param
 */
export const findSourceGameForTeam = (
  teamName: string | undefined,
  currentGameId: number,
  games: BracketGame[]
): number | null => {
  // Safety check - if no team name provided, return null
  if (!teamName) return null;

  // Only needed for games beyond the first round
  if (currentGameId < 32) return null;

  // Find games that feed into the current game
  const potentialSourceGames = games.filter(g => g.nextGameId === currentGameId);

  // Check if this team is a winner from any of those games
  for (const sourceGame of potentialSourceGames) {
    if (sourceGame.winner === teamName) {
      return sourceGame.id;
    }
  }

  return null;
};
/**
 * Initialize the bracket structure with proper seeding.
 * @param teams - Array of teams with seed and region info
 * @param roundNames - Optional array of round names from config context
 */
export const initializeBracket = (
  teams: Team[],
  roundNames: string[] = []
): BracketGame[] => {
  const games: BracketGame[] = [];
  const round1GamesCount = 32;

  // Create first round games (Round of 64)
  // Process each region separately to maintain proper seeding matchups
  const regions = [Region.SOUTH, Region.EAST, Region.WEST, Region.MIDWEST];

  regions.forEach((region, regionIndex) => {
    const regionTeams = teams.filter(team => team.region === region);

    // Create matchups according to standard tournament seeding:
    // 1 vs 16, 8 vs 9, 5 vs 12, 4 vs 13, 6 vs 11, 3 vs 14, 7 vs 10, 2 vs 15
    const matchups = [
      [1, 16], [8, 9], [5, 12], [4, 13], [6, 11], [3, 14], [7, 10], [2, 15]
    ];

    matchups.forEach((seedPair, pairIndex) => {
      const team1 = regionTeams.find(t => t.seed === seedPair[0]);
      const team2 = regionTeams.find(t => t.seed === seedPair[1]);

      if (team1 && team2) {
        // Calculate game ID based on region and matchup
        const gameId = regionIndex * 8 + pairIndex;

        games.push({
          id: gameId,
          round: 0,
          region,
          teamA: team1.name,
          teamB: team2.name,
          nextGameId: Math.floor(gameId / 2) + round1GamesCount
        });
      }
    });
  });

  // Sort the Round of 64 games by ID to ensure correct ordering
  games.sort((a, b) => a.id - b.id);

  // Create Round of 32 games
  for (let i = 0; i < 16; i++) {
    let region: Region;

    if (i < 4) region = Region.SOUTH;
    else if (i < 8) region = Region.EAST;
    else if (i < 12) region = Region.WEST;
    else region = Region.MIDWEST;

    games.push({
      id: i + round1GamesCount,
      round: 1,
      region,
      nextGameId: Math.floor(i / 2) + round1GamesCount + 16
    });
  }

  // Create Sweet 16 games
  for (let i = 0; i < 8; i++) {
    let region: Region;

    if (i < 2) region = Region.SOUTH;
    else if (i < 4) region = Region.EAST;
    else if (i < 6) region = Region.WEST;
    else region = Region.MIDWEST;

    games.push({
      id: i + round1GamesCount + 16,
      round: 2,
      region,
      nextGameId: Math.floor(i / 2) + round1GamesCount + 24
    });
  }

  // Create Elite 8 games
  for (let i = 0; i < 4; i++) {
    let region: Region;

    if (i === 0) region = Region.SOUTH;
    else if (i === 1) region = Region.EAST;
    else if (i === 2) region = Region.WEST;
    else region = Region.MIDWEST;

    games.push({
      id: i + round1GamesCount + 24,
      round: 3,
      region,
      nextGameId: Math.floor(i / 2) + round1GamesCount + 28
    });
  }

  // Create Final Four games - now using configurable name from context
  // Default to "Final Four" if no name is provided
  const finalFourName = roundNames[4] || "Final Four";
  
  games.push({
    id: round1GamesCount + 28,
    round: 4,
    region: finalFourName as any,
    nextGameId: round1GamesCount + 30
  });

  games.push({
    id: round1GamesCount + 29,
    round: 4,
    region: finalFourName as any,
    nextGameId: round1GamesCount + 30
  });

  // Create Championship game - now using configurable name from context
  // Default to "Championship" if no name is provided
  const championshipName = roundNames[5] || "Championship";
  
  games.push({
    id: round1GamesCount + 30,
    round: 5,
    region: championshipName as any
  });

  return games;
};

/**
 * Checks if a bracket is complete (all games have winners)
 */
export const isBracketComplete = (knownWinners: string[]): boolean => {
  // Count non-empty, non-placeholder winners
  const filledGames = knownWinners.filter(
    winner => winner && winner.trim() !== "" && winner !== "xxx"
  ).length;
  
  // A complete bracket has all 63 games filled
  return filledGames === 63;
};

/**
 * Checks if a round is fully completed (all games have winners).
 */
export const isRoundComplete = (games: BracketGame[], round: number): boolean => {
  const roundGames = games.filter(game => game.round === round);
  return roundGames.every(game => game.winner !== undefined);
};

/**
 * Checks if a round can be edited (has at least one game without a known winner).
 */
export const canEditRound = (games: BracketGame[], round: number, knownWinners: string[]): boolean => {
  const roundGames = games.filter(game => game.round === round);
  return roundGames.some(game => {
    // Check if this game needs user input (not a known winner)
    return game.id >= knownWinners.length || !knownWinners[game.id];
  });
};

/**
 * Groups games by round (not by region) for the accordion.
 */
export const groupGamesByRound = (games: BracketGame[]): Record<number, BracketGame[]> => {
  return games.reduce<Record<number, BracketGame[]>>((acc, game) => {
    // Skip Final Four (round 4) and championship game (round 5)
    if (game.round >= 4) return acc;
    
    if (!acc[game.round]) acc[game.round] = [];
    acc[game.round].push(game);
    return acc;
  }, {});
};

/**
 * Determines if a team in a game is from an official known winner path.
 * Only checks the immediate source game (parent) for this team.
 */
export const isTeamFromOfficialKnownWinnerPath = (
  teamName: string,
  gameId: number,
  knownWinners: WinnerWithMetadata[],
  games: BracketGame[]
): boolean => {
  // For first round games, always return false (these don't have source games)
  if (gameId < 32) return false;
  
  // Find the immediate source game for this team
  const sourceGameId = findSourceGameForTeam(teamName, gameId, games);
  
  // If no source game found, this team didn't come from anywhere (edge case)
  if (sourceGameId === null) return false;
  
  // Check if the source game has an official winner
  const sourceGame = games.find(g => g.id === sourceGameId);
  if (!sourceGame) return false;
  
  // Check if this source game has this team as an official known winner
  const winnerData = sourceGameId < knownWinners.length ? knownWinners[sourceGameId] : null;
  if (winnerData && winnerData.team === teamName && winnerData.source === WinnerSource.OFFICIAL) {
    return true; // This team is an official winner in its source game
  }
  
  // If we get here, this team is NOT from an official winner path
  return false;
  
  // REMOVED: The recursive check that was causing the issue
  // if (sourceGameId < 32) return false;
  // return isTeamFromOfficialKnownWinnerPath(teamName, sourceGameId, knownWinners, games);
}
